export type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "boolean"
  | "select"
  | "image";

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  defaultValue?: any;
  helper?: string;
  showInTable?: boolean;
  /** For select fields: fetch options from this admin endpoint. */
  optionsEndpoint?: string;
  optionsLabelKey?: string;
  allowEmpty?: boolean; // select can be empty (null)
}

export interface EntityConfig {
  key: string;
  title: string;
  subtitle: string;
  endpoint: string;
  fields: FieldDef[];
}

const common = {
  active: { name: "active", label: "Attivo", type: "boolean" as FieldType, defaultValue: true, showInTable: true },
  published: { name: "published", label: "Pubblicato", type: "boolean" as FieldType, defaultValue: true, showInTable: true },
  sort: { name: "sort_order", label: "Ordine", type: "number" as FieldType, defaultValue: 0, showInTable: true },
};

export const ENTITIES: Record<string, EntityConfig> = {
  themes: {
    key: "themes",
    title: "Temi",
    subtitle: "Temi grafici del configuratore (incl. \u201cSolo Colori\u201d)",
    endpoint: "/admin/themes",
    fields: [
      { name: "name", label: "Nome", type: "text", required: true, showInTable: true },
      { name: "slug", label: "Slug", type: "text" },
      { name: "description", label: "Descrizione", type: "textarea" },
      { name: "is_colors_only", label: "Solo Colori", type: "boolean", defaultValue: false, showInTable: true },
      { name: "image_url", label: "Immagine", type: "image" },
      common.active,
      common.sort,
    ],
  },
  models: {
    key: "models",
    title: "Modelli",
    subtitle: "Modelli di sneaker disponibili",
    endpoint: "/admin/models",
    fields: [
      { name: "name", label: "Nome", type: "text", required: true, showInTable: true },
      { name: "brand", label: "Brand", type: "text", showInTable: true },
      { name: "description", label: "Descrizione", type: "textarea" },
      { name: "image_url", label: "Immagine", type: "image" },
      common.active,
      common.sort,
    ],
  },
  areas: {
    key: "areas",
    title: "Aree",
    subtitle: "Aree personalizzabili della sneaker",
    endpoint: "/admin/areas",
    fields: [
      { name: "name", label: "Nome", type: "text", required: true, showInTable: true },
      { name: "description", label: "Descrizione", type: "textarea" },
      common.active,
      common.sort,
    ],
  },
  types: {
    key: "types",
    title: "Tipologie",
    subtitle: "Tipologie di personalizzazione",
    endpoint: "/admin/types",
    fields: [
      { name: "name", label: "Nome", type: "text", required: true, showInTable: true },
      { name: "area_id", label: "Area", type: "select", optionsEndpoint: "/admin/areas", optionsLabelKey: "name", allowEmpty: true, showInTable: true },
      { name: "description", label: "Descrizione", type: "textarea" },
      common.active,
      common.sort,
    ],
  },
  details: {
    key: "details",
    title: "Dettagli",
    subtitle: "Dettagli di personalizzazione (price_modifier = solo supporto CMS)",
    endpoint: "/admin/details",
    fields: [
      { name: "name", label: "Nome", type: "text", required: true, showInTable: true },
      { name: "type_id", label: "Tipologia", type: "select", optionsEndpoint: "/admin/types", optionsLabelKey: "name", allowEmpty: true, showInTable: true },
      { name: "description", label: "Descrizione", type: "textarea" },
      { name: "price_modifier", label: "Price modifier (\u20ac)", type: "number", defaultValue: 0, showInTable: true, helper: "Solo supporto CMS: il prezzo reale viene dalla Matrice Prezzi." },
      common.active,
      common.sort,
    ],
  },
  price_rules: {
    key: "price_rules",
    title: "Matrice Prezzi",
    subtitle: "Prezzo (min/max) per combinazione Tema+Modello+Area+Tipologia+Dettaglio",
    endpoint: "/admin/price-rules",
    fields: [
      { name: "theme_id", label: "Tema", type: "select", optionsEndpoint: "/admin/themes", optionsLabelKey: "name", allowEmpty: true, showInTable: true },
      { name: "model_id", label: "Modello", type: "select", optionsEndpoint: "/admin/models", optionsLabelKey: "name", allowEmpty: true, showInTable: true },
      { name: "area_id", label: "Area", type: "select", optionsEndpoint: "/admin/areas", optionsLabelKey: "name", allowEmpty: true, showInTable: true },
      { name: "type_id", label: "Tipologia", type: "select", optionsEndpoint: "/admin/types", optionsLabelKey: "name", allowEmpty: true, showInTable: true },
      { name: "detail_id", label: "Dettaglio", type: "select", optionsEndpoint: "/admin/details", optionsLabelKey: "name", allowEmpty: true, showInTable: true },
      { name: "price_min", label: "Prezzo min (\u20ac)", type: "number", defaultValue: 0, showInTable: true },
      { name: "price_max", label: "Prezzo max (\u20ac)", type: "number", defaultValue: 0, showInTable: true },
      common.active,
    ],
  },
  portfolio: {
    key: "portfolio",
    title: "Portfolio",
    subtitle: "Progetti realizzati (soft delete)",
    endpoint: "/admin/portfolio",
    fields: [
      { name: "title", label: "Titolo", type: "text", required: true, showInTable: true },
      { name: "slug", label: "Slug", type: "text" },
      { name: "description", label: "Descrizione", type: "textarea" },
      { name: "cover_image_url", label: "Immagine di copertina", type: "image", showInTable: true },
      { name: "theme_id", label: "Tema", type: "select", optionsEndpoint: "/admin/themes", optionsLabelKey: "name", allowEmpty: true },
      { name: "model_id", label: "Modello", type: "select", optionsEndpoint: "/admin/models", optionsLabelKey: "name", allowEmpty: true },
      { name: "featured", label: "In evidenza", type: "boolean", defaultValue: false, showInTable: true },
      common.published,
      common.sort,
    ],
  },
  faqs: {
    key: "faqs",
    title: "FAQ",
    subtitle: "Domande frequenti",
    endpoint: "/admin/faqs",
    fields: [
      { name: "question", label: "Domanda", type: "text", required: true, showInTable: true },
      { name: "answer", label: "Risposta", type: "textarea", required: true },
      common.published,
      common.sort,
    ],
  },
  reviews: {
    key: "reviews",
    title: "Cosa dicono i clienti",
    subtitle: "Testimonianze dei clienti",
    endpoint: "/admin/reviews",
    fields: [
      { name: "author_name", label: "Nome cliente", type: "text", required: true, showInTable: true },
      { name: "text", label: "Testo della testimonianza", type: "textarea", required: true },
      common.published,
      common.sort,
    ],
  },
};
